// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useId, useState } from 'react'
import clsx from 'clsx'
import styles from './OwnerHint.module.css'
import type { OwnerHintText } from 'lib/owner-notes/hints'

interface OwnerHintProps {
  /** Owner hints are plain strings; other callers may put links inside the text. */
  hint: Omit<OwnerHintText, 'text'> & { text: React.ReactNode }
  /** Anchor the bubble to the trigger's right edge (for a trigger at the row's end). */
  align?: 'start' | 'end'
  className?: string
  children: React.ReactNode
}

/**
 * A word with an explanation: hover or focus shows a one-line bubble sourced from
 * /how-we-verify-owners/ with a "Read more" link into the matching section; a tap
 * toggles it for touch. The bubble is in the server HTML (hidden), so the explanation is
 * also what a text-only reader or a crawler sees next to the term.
 */
const OwnerHint: React.VFC<OwnerHintProps> = ({ hint, align = 'start', className, children }) => {
  const id = useId()
  const [open, setOpen] = useState(false)
  return (
    <span className={clsx(styles.wrap, className)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className={styles.trigger}
        aria-describedby={id}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </button>
      <span
        role="tooltip"
        id={id}
        className={clsx(styles.tip, { [styles.tipEnd]: align === 'end', [styles.tipOpen]: open })}
      >
        {hint.text}{' '}
        <a href={hint.href} className={styles.link} tabIndex={open ? 0 : -1}>
          {'Read more'}
        </a>
      </span>
    </span>
  )
}

export default OwnerHint
